import * as UI from "@iyk/ui"
import * as React from "react"
import * as Dialog from "../../lib/ui/dialog.tsx"

import { toUpperCase } from "@iyk/string"
import { Link, useLocation } from "@remix-run/react"

export const ShareDialog = ({
  avatarUrl,
  creatorName,
  itemName = "",
  ownerName = "",
  ownerAvatarUrl = "",
  type,
}: ShareDialogProps) => {
  const location = useLocation()
  const [isCopied, setIsCopied] = React.useState(false)

  const shareUrl =
    typeof window !== "undefined"
      ? new URL(location.pathname, window.location.origin).toString()
      : ""

  const shareText = getShareText({
    type,
    creatorName,
    itemName,
    ownerName,
    link: shareUrl,
  })

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error("Failed to copy:", error)
    }
  }

  const isCreator = type === "creator"
  const avatarVariant = type === "user" ? "circle" : "square"

  return (
    <Dialog.Root>
      <Dialog.Content className={classForShareDialogContent({ type })} size="sm">
        <Dialog.Title hidden>Share</Dialog.Title>
        <div className="flex flex-col justify-center gap-3">
          <UI.Avatar src={avatarUrl} variant={avatarVariant} className="size-10 self-center" />
          <div className="flex flex-col gap-0.5">
            <span className={isCreator ? "text-base" : "text-xs"}>
              {isCreator ? creatorName : toUpperCase(creatorName)}
            </span>
            {!isCreator && <span>{itemName}</span>}
          </div>
        </div>
        {ownerName && (
          <UI.Badge kind="shaded">
            Owned by <UI.Avatar src={ownerAvatarUrl} variant="circle" className="size-4" />{" "}
            {ownerName}
          </UI.Badge>
        )}
        <div className="flex gap-2">
          <UI.Button type="button" kind="ghost" size="md" onClick={copyToClipboard}>
            {isCopied ? <UI.Icon.Check /> : <UI.Icon.Copy />}
          </UI.Button>

          {["twitter", "farcaster"].map((platform) => (
            <Link
              key={platform}
              to={
                platform === "twitter"
                  ? getTwitterShareURL({
                      text: shareText,
                      url: shareUrl,
                      via: "iyk_app",
                    }).toString()
                  : getFarcasterShareURL({
                      text: shareText,
                      embeds: [shareUrl],
                    }).toString()
              }
              className={UI.classForButton({ kind: "ghost", size: "md" })}
              target="_blank"
              rel="noreferrer"
            >
              {platform === "twitter" ? <UI.Icon.X /> : <UI.Icon.Farcaster />}
            </Link>
          ))}
        </div>
      </Dialog.Content>
      <Dialog.Trigger asChild>
        <UI.Button type="button" kind="ghost" size="sm">
          <UI.Icon.Share />
        </UI.Button>
      </Dialog.Trigger>
    </Dialog.Root>
  )
}

const classForShareDialogContent = UI.cva(["text-center flex flex-col items-center p-6"], {
  variants: {
    type: {
      itemDetail: "gap-4",
      chipDetail: "gap-6",
      creator: "gap-6",
      user: "gap-6",
    },
  },
})

function getShareText({
  type,
  itemName,
  ownerName,
  creatorName,
  link,
}: Pick<ShareDialogProps, "type" | "itemName" | "ownerName" | "creatorName"> & { link: string }) {
  switch (type) {
    case "chipDetail":
      return `Check out this ${itemName} owned by ${ownerName}! Explore its unique features and learn more about how it's being used: ${link} #Digiphysical #IYK`
    case "creator":
    case "user":
      return `${creatorName} is connecting physical and digital worlds. Check them out on IYK to see what they have been up to.`
    case "itemDetail":
      return `${creatorName} is connecting physical and digital worlds. Check out their ${itemName} at IYK`
    default:
      return "Unknown type"
  }
}

function getTwitterShareURL({
  text = "",
  url = "",
  hashtags = [],
  via = "",
  related = [],
}: {
  text?: string
  url?: string
  hashtags?: string[]
  via?: string
  related?: { username: string; description?: string }[]
}) {
  const shareUrl = new URL("https://x.com/intent/tweet")

  text && shareUrl.searchParams.append("text", text)
  url && shareUrl.searchParams.append("url", url)
  hashtags.length && shareUrl.searchParams.append("hashtags", hashtags.join(","))
  via && shareUrl.searchParams.append("via", via)

  if (related.length) {
    const relatedString = related
      .map(({ username, description }) => (description ? `${username}:${description}` : username))
      .join(",")
    shareUrl.searchParams.append("related", relatedString)
  }

  return shareUrl
}

function getFarcasterShareURL({
  text = "",
  embeds = [],
  channelKey = "",
  parentCastHash = "",
}: {
  text?: string
  embeds?: string[]
  channelKey?: string
  parentCastHash?: string
}) {
  const shareUrl = new URL("https://warpcast.com/~/compose")

  text && shareUrl.searchParams.append("text", text)

  embeds.forEach((embed) => shareUrl.searchParams.append("embeds[]", embed))

  channelKey && shareUrl.searchParams.append("channelKey", channelKey)
  parentCastHash && shareUrl.searchParams.append("parentCastHash", parentCastHash)

  return shareUrl
}

type ShareDialogProps = {
  avatarUrl: string
  creatorName: string
  itemName?: string
  ownerName?: string
  ownerAvatarUrl?: string
  type: "creator" | "chipDetail" | "itemDetail" | "user"
}
